import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Twitter, Facebook, Mail, Link as LinkIcon, Play, Image as ImageIcon } from "lucide-react";
import ReactPlayer from "react-player";
import { useToast } from "@/hooks/use-toast";
import logoImage from "@assets/legacyloop-white-trans.png";

interface SharePageProps {
  params?: { shareCode: string };
}

interface LoopResponse {
  member: { name: string; relationship: string; accessLevel: string };
  athlete: { name: string; sports?: string; position?: string; school?: string; graduationYear?: string; bio?: string };
  media: Array<{ id: string; mediaUrl: string; mediaType: string; description?: string; isPrivate?: boolean }>;
  journal: Array<{ id: string; title: string; content: string; createdAt: string }>;
}

const SharePage = ({ params }: SharePageProps) => {
  const shareCode = params?.shareCode ?? "";
  const { toast } = useToast();

  const { data, isLoading, isError } = useQuery<LoopResponse>({
    queryKey: ["/api/loop", shareCode],
    queryFn: async () => {
      const res = await fetch(`/api/loop/${shareCode}`);
      if (!res.ok) throw new Error("not_found");
      return res.json();
    },
    enabled: !!shareCode,
    retry: false,
  });

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(
      () => toast({ title: "Link copied", description: "Share link copied to clipboard." }),
      () => toast({ title: "Failed to copy", variant: "destructive" }),
    );
  };

  const handleTwitter = () => {
    const text = `Check out ${data?.athlete.name}'s highlights on LegacyLoop!`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`, "_blank");
  };

  const handleFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank");
  };

  const handleEmail = () => {
    const subject = `Check out ${data?.athlete.name}'s highlights`;
    const body = `I wanted to share these highlights with you: ${window.location.href}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
  };

  if (!shareCode || isError) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex flex-col items-center justify-center px-4 text-center">
        <img src={logoImage} alt="LegacyLoop" className="h-10 mb-6 opacity-80" />
        <h1 className="font-montserrat font-bold text-2xl text-white mb-2">This loop isn't available</h1>
        <p className="text-slate-400 text-sm max-w-sm">
          This share link may have expired or been removed. Ask the family to send you a new one.
        </p>
        <a href="/" className="mt-6 text-[#E0A458] hover:text-[#f0b467] text-sm font-medium">
          Learn about LegacyLoop
        </a>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex flex-col items-center justify-center gap-4">
        <img src={logoImage} alt="LegacyLoop" className="h-10 opacity-80" />
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-2 h-2 rounded-full bg-[#E0A458] animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
          ))}
        </div>
      </div>
    );
  }

  const { athlete, media } = data!;
  const visibleMedia = media.filter((m) => !m.isPrivate);
  const athleteSport = athlete.sports || "";
  const athleteMeta = [athleteSport, athlete.school, athlete.graduationYear ? `Class of ${athlete.graduationYear}` : ""].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white">

      {/* Top bar */}
      <div className="h-1.5 bg-[#E0A458]" />

      {/* Header */}
      <div className="bg-[#1B263B] border-b border-[#E0A458]/20 px-4 py-3 flex items-center justify-between">
        <img src={logoImage} alt="LegacyLoop" className="h-8" />
        <a
          href="/"
          className="text-xs font-semibold text-[#E0A458] hover:text-[#f0b467] border border-[#E0A458]/40 rounded-full px-3 py-1"
        >
          Create Your Own
        </a>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Athlete identity */}
        <div className="rounded-xl border border-[#E0A458]/20 bg-[#1B263B] px-6 py-5 mb-6">
          <h1 className="font-montserrat font-bold text-2xl text-white">{athlete.name}</h1>
          {athleteMeta && <p className="text-[#E0A458]/90 text-sm font-medium mt-0.5">{athleteMeta}</p>}
          {athlete.bio && <p className="text-slate-300 text-sm mt-3 leading-relaxed">{athlete.bio}</p>}
        </div>

        {/* Media */}
        {visibleMedia.length === 0 ? (
          <div className="rounded-xl border border-[#E0A458]/20 bg-[#1B263B] p-10 text-center mb-6">
            <ImageIcon className="h-8 w-8 text-[#E0A458]/50 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">No media shared yet.</p>
            <p className="text-sm text-slate-400 mt-1">Check back when the family adds highlights.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {visibleMedia.map((item) => {
              const isVideo = item.mediaType === "video";
              return (
                <div key={item.id} className="rounded-xl overflow-hidden border border-[#E0A458]/20 bg-[#1B263B]">
                  {isVideo ? (
                    <div className="aspect-video">
                      <ReactPlayer
                        url={item.mediaUrl}
                        controls
                        width="100%"
                        height="100%"
                        light
                        playIcon={
                          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#E0A458]/90">
                            <Play className="h-6 w-6 text-black fill-black" />
                          </div>
                        }
                      />
                    </div>
                  ) : (
                    <img
                      src={item.mediaUrl}
                      alt={item.description || athlete.name}
                      className="w-full aspect-video object-cover"
                    />
                  )}
                  {item.description && (
                    <p className="px-3 py-2 text-xs text-slate-400">{item.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Share actions */}
        <div className="rounded-xl border border-[#E0A458]/20 bg-[#1B263B] px-5 py-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-slate-300 font-medium">Share this loop</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handleTwitter} title="Share on X" className="border-[#415A77] bg-transparent text-slate-300 hover:text-white hover:border-[#E0A458]/60">
              <Twitter className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleFacebook} title="Share on Facebook" className="border-[#415A77] bg-transparent text-slate-300 hover:text-white hover:border-[#E0A458]/60">
              <Facebook className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleEmail} title="Share via Email" className="border-[#415A77] bg-transparent text-slate-300 hover:text-white hover:border-[#E0A458]/60">
              <Mail className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleCopyLink} title="Copy link" className="border-[#415A77] bg-transparent text-slate-300 hover:text-white hover:border-[#E0A458]/60">
              <LinkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* CTA */}
        <p className="text-center text-slate-500 text-xs mt-8">
          Shared via{" "}
          <a href="/" className="text-[#E0A458] hover:text-[#f0b467]">LegacyLoop</a>
          {" "}— The Private ESPN of Your Kid's Journey
        </p>
      </div>

      <div className="h-1.5 bg-[#E0A458]" />
    </div>
  );
};

export default SharePage;
